'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';

/**
 * A row from GET /posts/votes/{postID}, matching the live payload:
 *
 *   { id, value, createdAt, fullName, profilePictureURL,
 *     user: { id, username } }
 *
 * Note the id of the person is `user.id` — the row itself has no `userID`.
 * Reading the wrong one is what produced /profile/undefined.
 */
export interface PostLiker {
  id: string;
  /** 1 for an upvote; the endpoint is votes, not likes. */
  value?: number;
  createdAt?: string;
  fullName?: string | null;
  profilePictureURL?: string | null;
  user?: {
    id?: string;
    username?: string;
  };
  /** Not sent today; tolerated in case another caller shapes it flat. */
  userID?: string;
}

export interface PostLikersResponse {
  responseObject: {
    rows: PostLiker[];
    count: number;
  };
}

/** The profile this row links to. `user.id` first — the row has no userID. */
export function likerUserId(l: PostLiker): string | undefined {
  return l.user?.id ?? l.userID ?? undefined;
}

export function likerName(l: PostLiker): string {
  const name = (l.fullName ?? '').trim();
  if (name) return name;
  // `username` is a handle like "Flutter#7600" — strip the discriminator
  // rather than show it raw.
  const handle = (l.user?.username ?? '').split('#')[0].trim();
  if (handle) return handle;
  const id = likerUserId(l);
  return id ? `User ${id.slice(-4)}` : 'Member';
}

export function likerAvatar(l: PostLiker): string | null {
  return l.profilePictureURL ?? null;
}

export const usePostLikers = ({
  postID,
  limit = 20,
  enabled = true,
}: {
  postID: string;
  limit?: number;
  enabled?: boolean;
}) => {
  const { getAPIRequestTokenHeaders } = useAuth();

  return useInfiniteQuery<PostLikersResponse>({
    queryKey: ['posts', postID, 'votes', 'infinite'],
    queryFn: ({ pageParam = 0 }) =>
      Axios.get(`/posts/votes/${postID}`, {
        headers: getAPIRequestTokenHeaders(),
        params: { limit, offset: pageParam },
      }).then((res) => res.data),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.flatMap((p) => p.responseObject?.rows || []).length;
      const total = lastPage.responseObject?.count ?? 0;
      return loaded < total ? loaded : undefined;
    },
    initialPageParam: 0,
    // Only fetch when the dialog is actually open — this hangs off a button in
    // every post card in the feed.
    enabled: enabled && !!postID,
  });
};
